import { SafeError, SafePromise } from '@benzinga/safe-await';
import { ExtendedSubscribable } from '@benzinga/subscribable';
import { LavapressRestful } from './restful/lavapress';
import { SegmentProfileResponse, UserRestful } from './restful';
import {
  UserEditInfo,
  UserAvatar,
  UserAvatarPost,
  NotificationChannelsResponse,
  ToggleSubscriptionResponse,
  TOSAcceptance,
} from './entities';
import { ingressUser, Session } from '@benzinga/session';
import { User } from '@benzinga/session';
import { ingestAvatar } from './ingress';

interface ErrorEvent {
  error: SafeError;
  errorType:
    | 'avatar_update_error'
    | 'edit_user_error'
    | 'get_settings_error'
    | 'set_setting_error'
    | 'declare_market_interest_error'
    | 'set_market_interest_error'
    | 'get_notification_channels_error'
    | 'toggle_subscription_error';
  type: 'error';
}

export interface AvatarUpdateEvent {
  result: UserAvatar;
  type: 'avatar_updated';
}

export interface UserSettingsEvent {
  settings: unknown;
  type: 'received_user_settings';
}

interface NoDataEvent {
  type: 'updated_user';
}

export interface NotificationChannelsReceivedEvent {
  channels: NotificationChannelsResponse;
  type: 'notification_channels_received';
}

export interface SubscriptionToggledEvent {
  id: string | number;
  topicType: 'channel' | 'messageType';
  result: ToggleSubscriptionResponse;
  type: 'subscription_toggled';
}

export type UserRestfulEvent =
  | ErrorEvent
  | AvatarUpdateEvent
  | NoDataEvent
  | UserSettingsEvent
  | NotificationChannelsReceivedEvent
  | SubscriptionToggledEvent;

interface UserFunctions {
  updateAvatar: UserRequest['updateAvatar'];
  editUser: UserRequest['editUser'];
  getNotificationChannels: UserRequest['getNotificationChannels'];
  toggleSubscription: UserRequest['toggleSubscription'];
}

export class UserRequest extends ExtendedSubscribable<UserRestfulEvent, UserFunctions> {
  private restful: UserRestful;
  private lavapressRestful: LavapressRestful;

  constructor(session: Session) {
    super();
    this.restful = new UserRestful(session);
    this.lavapressRestful = new LavapressRestful(session);
  }

  public updateAvatar = async (avatarPost: UserAvatarPost): SafePromise<UserAvatar> => {
    const response = await ingestAvatar(this.restful.updateAvatar(avatarPost));
    if (response.err) {
      this.dispatch({
        error: response.err,
        errorType: 'avatar_update_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        result: response.ok,
        type: 'avatar_updated',
      });
    }
    return response;
  };

  public editUser = async (user: UserEditInfo): SafePromise<User> => {
    const res = await this.restful.editUser(user);
    if (res.err) {
      this.dispatch({
        error: res.err,
        errorType: 'edit_user_error',
        type: 'error',
      });
      return res;
    }
    const normalizedUser = ingressUser(res.ok);
    this.dispatch({
      type: 'updated_user',
    });

    return { ok: normalizedUser };
  };

  public getGlobalSettings = async (): SafePromise<object> => {
    const res = await this.restful.getGlobalSettings();
    if (res.err) {
      this.dispatch({
        error: res.err,
        errorType: 'get_settings_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        settings: res.ok,
        type: 'received_user_settings',
      });
    }
    return res;
  };

  public setGlobalSettings = async (key: string, value: unknown): SafePromise<undefined> => {
    const res = await this.restful.setGlobalSetting(key, value);
    if (res.err) {
      this.dispatch({
        error: res.err,
        errorType: 'set_setting_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        settings: res.ok,
        type: 'received_user_settings',
      });
    }
    return res;
  };

  public updateProperty = async (email: string, property: string, value: string) => {
    const res = await this.lavapressRestful.updateProperty(email, property, value);
    return res;
  };

  public getSegmentUserTraits = async (params?: {
    userId?: string;
    anonymousId?: string;
  }): SafePromise<SegmentProfileResponse> => {
    return this.restful.getSegmentUserTraits(params);
  };

  public declareMarketInterest = async (interest: string) => {
    const res = await this.restful.declareMarketInterest(interest);
    if (res.err) {
      this.dispatch({
        error: res.err,
        errorType: 'declare_market_interest_error',
        type: 'error',
      });
    }
    return res;
  };

  public deleteAccount = (): SafePromise<unknown> => {
    return this.restful.deleteAccount();
  };

  public captureTOSAcceptance = (tosAcceptance: TOSAcceptance): SafePromise<unknown> => {
    return this.restful.captureTOSAcceptance(tosAcceptance);
  };

  public getNotificationChannels = async (): SafePromise<NotificationChannelsResponse> => {
    const res = await this.restful.getNotificationChannels();
    if (res.err) {
      this.dispatch({
        error: res.err,
        errorType: 'get_notification_channels_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        channels: res.ok,
        type: 'notification_channels_received',
      });
    }
    return res;
  };

  public toggleSubscription = async (
    id: string | number,
    topicType: 'channel' | 'messageType',
  ): SafePromise<ToggleSubscriptionResponse> => {
    const res = await this.restful.toggleSubscription({ id, topic_type: topicType });
    if (res.err) {
      this.dispatch({
        error: res.err,
        errorType: 'toggle_subscription_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        id,
        result: res.ok,
        topicType,
        type: 'subscription_toggled',
      });
    }
    return res;
  };

  public onSubscribe = (): UserFunctions => ({
    editUser: this.editUser,
    getNotificationChannels: this.getNotificationChannels,
    toggleSubscription: this.toggleSubscription,
    updateAvatar: this.updateAvatar,
  });
}
